import useHttpRequest from './HttpRequest';
import { ITEM_CONVERT_CATALOG } from '../rewards/common/Constants';

/**
 * Which catalog to read. One route serves them all, so they differ by this alone — and so a
 * region refusal is recovered from the same way whichever one the buyer was shopping in.
 */
export const CatalogFeature = {
  PURCHASE: 'purchase',
  SUBSCRIPTIONS: 'subscriptions',
};

/**
 * Catalogs are priced per region and served per feature, so the path is built rather than
 * fixed. Exported because the catalog is read both from a component (this hook) and from a
 * Recoil selector, and the two must ask for the same thing.
 *
 * @param {object} [filters]
 * @param {string} [filters.feature] catalog to read, the purchase one by default
 * @param {string} [filters.region] pricing region to read the catalog for
 * @param {string} [filters.sku] narrow to one product's SKU
 * @returns {string} path with query string
 */
export const buildCatalogPath = ({ feature = CatalogFeature.PURCHASE, region, sku } = {}) => {
  const query = new URLSearchParams({
    ...region && { region },
    ...sku && { sku },
  }).toString();

  const path = `/web-payments/${feature}/web/catalog`;

  return query ? `${path}?${query}` : path;
};

const useCatalogAPI = () => {
  const httpRequest = useHttpRequest();

  /**
   * @param {object} [filters] see `buildCatalogPath`
   * @throws {Response} when the catalog could not be read
   * @returns {Promise<Array<object>>} products
   */
  const readCatalog = async (filters) => {
    const { items = [] } = await httpRequest.send(buildCatalogPath(filters));

    return items;
  };

  /**
   * The catalog on screen, where a failed read has nowhere to go: there is no state for it, so
   * it degrades to an empty list. Callers that can tell the buyer something useful about the
   * failure — or that would otherwise mistake it for "nothing is sold here" — should read
   * through `readCatalog` and handle the rejection themselves.
   *
   * @param {object} [filters] see `buildCatalogPath`
   * @returns {Promise<Array<object>>} products, empty when the read failed
   */
  const fetchCatalog = async (filters) => {
    try {
      return await readCatalog(filters);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error fetching the catalog', filters, err);
      return [];
    }
  };

  /**
   * @param {object} [filters] see `buildCatalogPath`, minus the feature
   * @returns {Promise<Array<object>>} products, empty when the read failed
   */
  const fetchPurchaseCatalog = (filters) => fetchCatalog({
    ...filters,
    feature: CatalogFeature.PURCHASE,
  });

  const fetchCashoutCatalog = async (currency) => {
    if (!currency) return [];

    try {
      const { items } = await httpRequest.send(`/web-payments/cash-reward/catalog?currency=${currency}&cursor=0&limit=500`);

      // eslint-disable-next-line no-console
      console.log('product catalog result', items);
      return items;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Unable to fetch cash-out packages');
      return [];
    }
  };

  const fetchConvertCatalog = async (currency) => {
    if (!currency) return [];

    try {
      const { items } = await httpRequest.send(
        `/web-payments/convert/catalog?currency=${currency}&cursor=0&limit=500`,
      );

      // eslint-disable-next-line no-console
      console.log('product catalog result', items);
      return items;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Unable to fetch cash-out packages');
      return [];
    }
  };

  /* Unlike the convert catalog, there is no `currency` filter to apply: every item exchanges
   * into its own consumable currency, so the catalog itself scopes the response.
   */
  const fetchItemConvertCatalog = async () => {
    try {
      const { items } = await httpRequest.send(
        `/web-payments/${ITEM_CONVERT_CATALOG}/catalog?cursor=0&limit=500`,
      );

      // eslint-disable-next-line no-console
      console.log('items catalog result', items);
      return items;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Unable to fetch item packages');
      return [];
    }
  };

  /**
   * @param {string} type subscription category the items are narrowed to
   * @param {object} [filters] see `buildCatalogPath`, minus the feature
   * @returns {Promise<object>} the catalog response, its items empty when the read failed
   */
  const fetchSubscriptionCatalogByType = async (type, filters) => {
    try {
      const result = await httpRequest.send(buildCatalogPath({
        ...filters,
        feature: CatalogFeature.SUBSCRIPTIONS,
      }));
      const {
        items = [],
      } = result;

      // todo: need to extend an API to filter by type
      const filteredItems = items.filter((it) => it.categories.includes(type));
      // eslint-disable-next-line no-console
      console.log(`Subscription catalog result for type: ${type}`, result);
      return {
        ...result,
        items: filteredItems,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(`Error fetching subscriptions catalog for type: ${type}`, err);
      return { items: [] };
    }
  };

  return {
    readCatalog,
    fetchCatalog,
    fetchPurchaseCatalog,
    fetchCashoutCatalog,
    fetchConvertCatalog,
    fetchItemConvertCatalog,
    fetchSubscriptionCatalogByType,
  };
};

export default useCatalogAPI;
